import { allCards, getSetting, reviewsSince, setSetting, type CardRecord } from './db';
import { parseKey, retrievability, State } from './fsrs';
import { t } from '../strings';
import { SKILLS, type Skill } from '../types';

// A card counts toward "mastery" once it is in the Review state and its current
// retrievability is above this threshold — i.e. the learner would very likely
// recall it right now.
const MASTERY_RETRIEVABILITY = 0.9;

export const XP_PER_CORRECT = 10;
export const XP_DAILY_BONUS = 30;

export interface SkillMastery {
  /** Cards in Review state and currently above the retrievability threshold. */
  mastered: number;
  /** Species this skill has actually put in front of the learner. */
  started: number;
  /** Species in the selected pack(s) — the ceiling, not the denominator. See below. */
  total: number;
  /** `mastered / started`, 0–100. */
  percent: number;
}

export interface MasteryReport {
  bySkill: Record<Skill, SkillMastery>;
  /** Distinct species met in at least one skill. */
  speciesStarted: number;
  /** Species in the selected pack(s). */
  total: number;
  /** Mean of the three skill percentages — the "Mastery" stat. */
  overall: number;
}

/**
 * Mastery per skill, measured against the birds the learner has actually met.
 *
 * The denominator used to be the whole region, which made the ring a coverage
 * meter — and meant every batch of species added to the app knocked points off
 * a number the learner had done nothing to lose. Going from 120 to 150 birds
 * silently cut a 60% ring to 48%, which reads as forgetting.
 *
 * So the ring now answers "of the birds you've been shown, how many are solid?"
 * — a number only study can move. Coverage doesn't disappear: `started` and
 * `total` come back alongside it and the Home screen states them outright, so
 * growth shows up as birds left to meet rather than as a ring going backwards.
 */
export async function masteryBySkill(
  activeCodes: string[],
  now: Date = new Date(),
): Promise<MasteryReport> {
  const cards = await allCards();
  const active = new Set(activeCodes);
  const mastered: Record<Skill, number> = { sound: 0, names: 0, photo: 0 };
  const started: Record<Skill, number> = { sound: 0, names: 0, photo: 0 };
  const codes = new Set<string>();

  for (const rec of cards) {
    // Only the packs currently selected. Cards outlive a selection — study Cape
    // Town for a week, switch back to the Pacific Northwest, and those cards are
    // still in the store — and counting them here would answer a question about
    // one pack with another pack's work. The old `Math.min` cap hid the overflow
    // rather than fixing it.
    if (!active.has(rec.code)) continue;
    // A card exists only once its species has come up in that skill's queue,
    // which is exactly what "met this bird" means here.
    started[rec.skill] += 1;
    codes.add(rec.code);
    if (rec.card.state !== State.Review) continue;
    if (retrievability(rec.card, now) >= MASTERY_RETRIEVABILITY) {
      mastered[rec.skill] += 1;
    }
  }

  // No cap needed any more. Cards are already filtered to `active` and are one
  // per (species, skill), so `started` cannot exceed the pack size — where it
  // used to, and was clamped, because a card outliving its species (a taxonomic
  // split, a region edit) counted against a region that no longer had it. That
  // is now the same exclusion as a card from an unselected pack.
  const total = active.size;
  const bySkill = {} as Record<Skill, SkillMastery>;
  for (const skill of SKILLS) {
    const start = started[skill];
    bySkill[skill] = {
      mastered: mastered[skill],
      started: start,
      total,
      percent: start > 0 ? Math.round((mastered[skill] / start) * 100) : 0,
    };
  }

  return {
    bySkill,
    speciesStarted: codes.size,
    total,
    overall: Math.round(
      SKILLS.reduce((n, s) => n + bySkill[s].percent, 0) / SKILLS.length,
    ),
  };
}

// ---- Sound ID hard mode ----------------------------------------------------

/** Sound ID mastery, in percent, at which calls join the mix. */
export const HARD_SOUND_MASTERY = 30;
/**
 * Birds met in Sound ID before the percentage is allowed to unlock anything.
 *
 * Load-bearing, not a nicety: `percent` is the share of the birds you have MET
 * that are solid, so a learner who has met two birds and knows one of them is
 * sitting at 50%. Without a floor the hard mode would arrive on the third
 * question ever answered, which is precisely backwards.
 */
export const HARD_SOUND_MET = 20;

/**
 * Whether Sound ID has earned its calls.
 *
 * A pure read of a report the callers already have — Home renders the badge
 * from it and the quiz picks its recordings from it, and neither pays for a
 * second pass over the card set.
 */
export function soundHardUnlocked(m: MasteryReport): boolean {
  const s = m.bySkill.sound;
  return s.started >= HARD_SOUND_MET && s.percent >= HARD_SOUND_MASTERY;
}

// ---- XP + streak -----------------------------------------------------------

const K_XP = 'xp';
const K_STREAK = 'streak';
const K_LAST_DAY = 'lastReviewDay';
/** The week whose free rest day has been spent, named by its Monday. */
const K_REST_WEEK = 'restDayWeek';

/** Local calendar day as YYYY-MM-DD. */
export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  return Math.round((db.getTime() - da.getTime()) / 86_400_000);
}

export async function getXp(): Promise<number> {
  return (await getSetting<number>(K_XP)) ?? 0;
}

export async function addXp(amount: number): Promise<number> {
  const next = (await getXp()) + amount;
  await setSetting(K_XP, next);
  return next;
}

export async function getStreak(): Promise<number> {
  return (await getSetting<number>(K_STREAK)) ?? 0;
}

// ---- Rest days --------------------------------------------------------------
//
// One missed day a week does not break the streak.
//
// A streak that dies the first Tuesday you work late is a counter that punishes
// a good week for one bad evening, and the learner it punishes hardest is the
// one who was doing well — nobody with a two-day streak feels the loss. So each
// calendar week carries one free miss. It keeps the run alive; it does not count
// toward it, because the number is days STUDIED and a rest day is the opposite
// of that.
//
// Weekly rather than rolling: "one a week" is a rule a person can hold in their
// head and plan around, and the strip on the home screen can say how many are
// left without arithmetic.

/** Free missed days per calendar week. */
export const WEEKLY_REST_DAYS = 1;

/** The week a day belongs to, named by its Monday. Weeks run Monday–Sunday. */
export function weekKey(d: Date): string {
  const monday = new Date(d);
  monday.setHours(0, 0, 0, 0);
  // getDay(): 0 = Sunday. Sunday belongs to the week that started six days ago.
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return dayKey(monday);
}

/** The day before `key`, as a key. */
function dayBefore(key: string): string {
  const d = new Date(key + 'T00:00:00');
  d.setDate(d.getDate() - 1);
  return dayKey(d);
}

/**
 * Record that the user studied today and return the updated streak.
 *
 * Same day → unchanged; consecutive day → +1; ONE missed day → +1 and the
 * week's rest day is spent on it; anything longer → reset to 1.
 *
 * The rest day is charged to the week the MISSED day fell in, not to the week
 * you came back in — miss a Sunday and study on Monday and it is Sunday's week
 * that has been spent, which is the week you actually rested in.
 */
export async function touchStreak(now: Date = new Date()): Promise<number> {
  const today = dayKey(now);
  const [last, spentWeek] = await Promise.all([
    getSetting<string>(K_LAST_DAY),
    getSetting<string>(K_REST_WEEK),
  ]);
  let streak = await getStreak();

  const gap = last ? daysBetween(last, today) : Infinity;
  if (gap === 0) {
    // already counted today
  } else if (gap === 1) {
    streak += 1;
  } else if (gap === 2 && streak > 0) {
    // Exactly one day missed. It costs the rest day of the week it fell in, if
    // that week still has one.
    const missed = dayBefore(today);
    const week = weekKey(new Date(missed + 'T00:00:00'));
    if (spentWeek === week) {
      streak = 1; // already rested this week — the run really is over
    } else {
      streak += 1;
      await setSetting(K_REST_WEEK, week);
    }
  } else {
    streak = 1;
  }

  await setSetting(K_STREAK, streak);
  await setSetting(K_LAST_DAY, today);
  return streak;
}

/** Rest days still available in the week containing `now`. */
export async function restDaysLeft(now: Date = new Date()): Promise<number> {
  const spent = await getSetting<string>(K_REST_WEEK);
  return spent === weekKey(now) ? 0 : WEEKLY_REST_DAYS;
}

/**
 * What the streak looks like from outside: the number to show, and whether it
 * is still waiting on today.
 *
 * `getStreak` reads what was banked the last time somebody studied, which is
 * only the current streak until a day is missed — nothing runs at midnight to
 * expire it, so a five-day streak abandoned a week ago still reads "5" right up
 * until the next answer resets it to 1. Every screen that prints the number
 * wants the honest one, and the home screen wants the other half too: a live
 * streak with nothing answered yet today is one evening away from lapsing, and
 * saying so is the only thing a streak counter is really for.
 */
export interface StreakStatus {
  /** Days in a row, and 0 once a missed day has broken it. */
  days: number;
  /** Whether an answer has already landed today. */
  studiedToday: boolean;
  /** A live streak with nothing answered today — it lapses at midnight. */
  atRisk: boolean;
  /** Free missed days left in this calendar week (see WEEKLY_REST_DAYS). */
  restDaysLeft: number;
}

export async function streakStatus(now: Date = new Date()): Promise<StreakStatus> {
  const [days, last, spentWeek] = await Promise.all([
    getStreak(),
    getSetting<string>(K_LAST_DAY),
    getSetting<string>(K_REST_WEEK),
  ]);
  const today = dayKey(now);
  const left = spentWeek === weekKey(now) ? 0 : WEEKLY_REST_DAYS;
  if (last === today) {
    return { days, studiedToday: true, atRisk: false, restDaysLeft: left };
  }
  // Yesterday keeps the count alive until midnight. So does the day before that,
  // but only while the missed day's week still has its rest day to spend — the
  // spending itself happens on the next answer, in touchStreak, so this has to
  // ask the same question that will decide it.
  const gap = last ? daysBetween(last, today) : Infinity;
  const missedWeek = weekKey(new Date(dayBefore(today) + 'T00:00:00'));
  const alive =
    days > 0 && (gap === 1 || (gap === 2 && spentWeek !== missedWeek));
  return { days: alive ? days : 0, studiedToday: false, atRisk: alive, restDaysLeft: left };
}

/**
 * Whether the day's once-a-day XP bonus has been paid.
 *
 * The bonus used to belong to the daily challenge, a single rotating question
 * that had nothing to do with what was actually due. The challenge is gone and
 * the bonus moved to the first review round finished each day — same flag, same
 * once-a-day rule, now attached to clearing the queue rather than to answering
 * one question about one bird.
 */
const K_DAILY_DONE = 'dailyDoneDay';

export async function isDailyDone(now: Date = new Date()): Promise<boolean> {
  return (await getSetting<string>(K_DAILY_DONE)) === dayKey(now);
}

export async function markDailyDone(now: Date = new Date()): Promise<void> {
  await setSetting(K_DAILY_DONE, dayKey(now));
}

/** Number of reviews done during today's local calendar day. */
export async function reviewsToday(now: Date = new Date()): Promise<number> {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return (await reviewsSince(start.getTime())).length;
}

// ---- Charted history (the Stats screen) -------------------------------------
//
// Everything below reads the same two sources the rest of the app does — the
// card set and the review log — and shapes them for a chart. No new persistence:
// the review log has always carried the timestamp and the correct/incorrect
// flag, they were simply never read back for anything but "reviews today".
//
// One limit worth stating: the log is hydrated for the last 30 days only (see
// HYDRATE_REVIEW_DAYS in db.ts), so a window longer than that reads as empty
// days rather than as missing data. The Stats screen asks for 14.

/** Local midnight, `back` days before `now`. */
function midnight(now: Date, back = 0): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - back);
  return d;
}

export interface DayTally {
  /** Local calendar day, YYYY-MM-DD. */
  day: string;
  /** Reviews answered that day. */
  total: number;
  /** How many of them were right. */
  correct: number;
}

/**
 * Reviews per day over the last `days` days, oldest first and today last.
 * Days with no study are present with zeroes — a gap in a bar chart is a fact
 * about the week, not a missing column.
 */
export async function reviewHistory(
  days = 14,
  now: Date = new Date(),
): Promise<DayTally[]> {
  const from = midnight(now, days - 1);
  const reviews = await reviewsSince(from.getTime());
  const tallies = new Map<string, DayTally>();
  for (let i = days - 1; i >= 0; i--) {
    const day = dayKey(midnight(now, i));
    tallies.set(day, { day, total: 0, correct: 0 });
  }
  for (const r of reviews) {
    const t = tallies.get(dayKey(new Date(r.ts)));
    if (!t) continue;
    t.total += 1;
    if (r.correct) t.correct += 1;
  }
  return [...tallies.values()];
}

export interface SkillAccuracy {
  total: number;
  correct: number;
  /** 0–100, or null when the skill hasn't been reviewed in the window. */
  percent: number | null;
}

/**
 * How often answers were right, per skill, over the last `days` days.
 *
 * Deliberately distinct from mastery: this is what you did, mastery is what you
 * currently know. A week of hard new material can show a falling accuracy and a
 * rising mastery at the same time, and the Stats screen says so.
 *
 * Also deliberately NOT scoped to the selected packs, where mastery and the due
 * forecast both are. Those two describe a pack — what you know of it, what it
 * will ask next — and answering them with another pack's cards is simply wrong.
 * This describes a fortnight of your answering, which happened whichever pack
 * was selected at the time; it belongs with the streak and the XP, not with the
 * rings. Switching packs should no more reset your accuracy than your streak.
 */
export async function accuracyBySkill(
  days = 14,
  now: Date = new Date(),
): Promise<Record<Skill, SkillAccuracy>> {
  const reviews = await reviewsSince(midnight(now, days - 1).getTime());
  const out = {} as Record<Skill, SkillAccuracy>;
  for (const s of SKILLS) out[s] = { total: 0, correct: 0, percent: null };
  for (const r of reviews) {
    const { skill } = parseKey(r.key);
    const acc = out[skill];
    if (!acc) continue;
    acc.total += 1;
    if (r.correct) acc.correct += 1;
  }
  for (const s of SKILLS) {
    const acc = out[s];
    acc.percent = acc.total ? Math.round((acc.correct / acc.total) * 100) : null;
  }
  return out;
}

// ---- What gets mistaken for what -------------------------------------------
//
// The review log records the species the learner NAMED as well as the one they
// were asked (`picked` on ReviewRecord, from schema v2). Everything here is a
// read of that one field: a bird missed is a fact about a card, but a bird
// missed *as another bird* is a fact about a pair, and a pair is the thing a
// learner can actually do something about.
//
// Reviews written before v2 — and every flip-card grade, which has no pick to
// record — carry no `picked` and are simply not counted, in the numerator or
// the denominator. The list grows into itself over a fortnight of study rather
// than starting wrong.

/** One directional mix-up: `code` asked, `picked` named instead. */
export interface Confusion {
  /** The skill it happens in. Mixing two birds up by ear and by sight are
   *  different mistakes with different cures, so they are never pooled. */
  skill: Skill;
  /** The bird the question was about. */
  code: string;
  /** The bird named instead. */
  picked: string;
  /** How many times this exact substitution happened in the window. */
  times: number;
  /** Picks recorded on `code` in this skill over the same window. */
  asked: number;
  /** `times / asked`, 0–100 — the "four times out of five". */
  percent: number;
}

/**
 * How far back the mix-up list looks. Capped by what db.ts hydrates
 * (HYDRATE_REVIEW_DAYS), so asking for more would quietly read as fewer.
 */
export const CONFUSION_DAYS = 30;

/**
 * How often a pair has to have been mixed up before it is called a mix-up.
 *
 * One slip is a slip — a mis-tap, a guess on a bird met that morning — and
 * listing it would fill the screen with noise and send the pair drill after
 * birds the learner knows perfectly well. Two is the smallest number that can
 * be a pattern.
 */
export const CONFUSION_MIN = 2;

/**
 * Which birds get called which other birds, commonest mix-up first.
 *
 * Directional on purpose: calling a Pacific Wren a Winter Wren and calling a
 * Winter Wren a Pacific Wren are different states of knowledge, and a learner
 * who only ever makes the first mistake is told exactly that. The pair drill
 * merges the two directions again, because the cure is the same either way.
 *
 * Not scoped to the selected packs, for the same reason `accuracyBySkill` is
 * not: this describes answering that happened, whichever pack was loaded at the
 * time. Callers that need to draw a bird's name or ask about it again filter to
 * what is actually loaded.
 */
export async function confusions(
  days = CONFUSION_DAYS,
  now: Date = new Date(),
): Promise<Confusion[]> {
  const reviews = await reviewsSince(midnight(now, days - 1).getTime());
  const asked = new Map<string, number>();
  const pairs = new Map<string, Confusion>();

  for (const r of reviews) {
    if (!r.picked) continue;
    const { code, skill } = parseKey(r.key);
    if (!SKILLS.includes(skill)) continue;
    const askedKey = `${skill}:${code}`;
    asked.set(askedKey, (asked.get(askedKey) ?? 0) + 1);
    if (r.picked === code) continue;
    const pairKey = `${askedKey}:${r.picked}`;
    const seen = pairs.get(pairKey);
    if (seen) seen.times += 1;
    else pairs.set(pairKey, { skill, code, picked: r.picked, times: 1, asked: 0, percent: 0 });
  }

  const out: Confusion[] = [];
  for (const c of pairs.values()) {
    if (c.times < CONFUSION_MIN) continue;
    c.asked = asked.get(`${c.skill}:${c.code}`) ?? c.times;
    c.percent = Math.round((c.times / c.asked) * 100);
    out.push(c);
  }
  // Times first — a pair missed six times matters more than one missed twice
  // out of two — then the rate, then the code, so the order is stable between
  // renders rather than depending on Map insertion.
  return out.sort(
    (a, b) => b.times - a.times || b.percent - a.percent || a.code.localeCompare(b.code),
  );
}

/**
 * How far back the most-failed list looks. Capped by what db.ts hydrates
 * (HYDRATE_REVIEW_DAYS), same as `CONFUSION_DAYS`.
 *
 * A month rather than `MISSED_DAYS`' week, because this list ranks and a week
 * rarely holds enough answers about any one bird to rank on. The two are
 * different questions: Missed is "what went wrong lately", which a week
 * answers; most-failed is "which birds do I keep getting wrong", which needs
 * enough answers to see a pattern in.
 */
export const FAILED_DAYS = 30;

/**
 * How many wrong answers a bird needs before it is called a problem.
 *
 * One is a slip — a mis-tap, or a guess on a bird met that morning — and a list
 * of one-offs would be a list of everything recently introduced. Two is the
 * smallest number that can be a habit, which is the same reasoning and the same
 * floor as `CONFUSION_MIN`.
 */
export const FAILED_MIN = 2;

export interface FailedSpecies {
  code: string;
  /** Wrong answers about this bird in the window, across all three skills. */
  wrong: number;
  /** Times it was asked at all — the denominator behind `percent`. */
  asked: number;
  /** `wrong / asked`, 0–100. */
  percent: number;
}

/**
 * The birds that keep going wrong, worst first.
 *
 * Deliberately not the same list as `weakestSpecies`, which is next door and
 * sounds like it. That one reads the scheduler's model — its estimate of what
 * you would recall right now — and can name a bird you have never once got
 * wrong, simply because it has not been asked in a while. This one reads what
 * actually happened. A bird the model has written off but that you have in fact
 * been getting right does not belong on a list called "most failed", and a bird
 * you fail every third time belongs at the top of it even on the morning after
 * a correct answer has flattered its retrievability.
 *
 * Counted per bird rather than per card, because "I cannot do Winter Wren" is
 * how a learner experiences it, not "I cannot do Winter Wren by sound". The
 * Mix-ups drill next door is the per-pair version of the same evidence.
 *
 * Not scoped to the selected packs, for the same reason `confusions` is not:
 * this describes answering that happened, whichever pack was loaded at the
 * time. Callers narrow to what is actually loaded.
 */
export async function mostFailed(
  days = FAILED_DAYS,
  now: Date = new Date(),
  min = FAILED_MIN,
): Promise<FailedSpecies[]> {
  const reviews = await reviewsSince(midnight(now, days - 1).getTime());
  const tally = new Map<string, { wrong: number; asked: number }>();

  for (const r of reviews) {
    const { code, skill } = parseKey(r.key);
    if (!SKILLS.includes(skill)) continue;
    const at = tally.get(code) ?? { wrong: 0, asked: 0 };
    at.asked += 1;
    if (!r.correct) at.wrong += 1;
    tally.set(code, at);
  }

  const out: FailedSpecies[] = [];
  for (const [code, { wrong, asked }] of tally) {
    if (wrong < min) continue;
    out.push({ code, wrong, asked, percent: Math.round((wrong / asked) * 100) });
  }
  // Count first — a bird missed six times out of twelve is a bigger problem
  // than one missed twice out of two — then the rate, then the code, so the
  // order holds still between renders rather than following Map insertion.
  return out.sort(
    (a, b) => b.wrong - a.wrong || b.percent - a.percent || a.code.localeCompare(b.code),
  );
}

export interface DueForecast {
  /** Cards already past due right now. */
  overdue: number;
  /** One entry per day starting today, `count` cards falling due on it. */
  days: { day: string; count: number }[];
}

/**
 * What the schedule is about to ask for. Cards already overdue are counted
 * separately rather than piled onto today's column, because "you are 40 behind"
 * and "40 come up today" are different situations and the pile-up hid the first.
 */
export async function dueForecast(
  activeCodes: string[],
  days = 7,
  now: Date = new Date(),
): Promise<DueForecast> {
  const cards = await allCards();
  // Scoped like mastery is, and for a sharper reason: this is a promise about
  // what the app will ask next, and it will not ask about a pack that isn't
  // selected. An unscoped forecast would show a backlog no round can drain.
  const active = new Set(activeCodes);
  const buckets = new Map<string, number>();
  for (let i = 0; i < days; i++) {
    buckets.set(dayKey(midnight(now, -i)), 0);
  }
  const horizon = midnight(now, -days).getTime();
  let overdue = 0;
  for (const rec of cards) {
    if (!active.has(rec.code)) continue;
    const due = rec.due.getTime();
    if (due <= now.getTime()) {
      overdue += 1;
      continue;
    }
    if (due >= horizon) continue;
    const day = dayKey(new Date(due));
    const at = buckets.get(day);
    if (at !== undefined) buckets.set(day, at + 1);
  }
  return {
    overdue,
    days: [...buckets.entries()].map(([day, count]) => ({ day, count })),
  };
}

export interface SkillProgress {
  /** Cards in Review state and above the retrievability threshold. */
  solid: number;
  /** Species met in this skill but not yet solid. */
  shaky: number;
  /** Species in the selected pack(s) this skill has never asked about. */
  unmet: number;
}

/**
 * The selected packs split three ways per skill: solid, met-but-shaky, never met.
 *
 * `masteryBySkill` answers "how much of what I've met is solid" — one number,
 * and deliberately not a coverage meter. This is the other half of that story,
 * and the two together are what the Stats screen exists to explain.
 */
export async function skillProgress(
  activeCodes: string[],
  now: Date = new Date(),
): Promise<Record<Skill, SkillProgress>> {
  const report = await masteryBySkill(activeCodes, now);
  const out = {} as Record<Skill, SkillProgress>;
  for (const s of SKILLS) {
    const m = report.bySkill[s];
    out[s] = {
      solid: m.mastered,
      shaky: Math.max(0, m.started - m.mastered),
      unmet: Math.max(0, m.total - m.started),
    };
  }
  return out;
}

/**
 * How far back "missed" looks. Capped by what db.ts hydrates
 * (HYDRATE_REVIEW_DAYS), so asking for more would quietly read as fewer.
 *
 * A week because it is the span a learner can still remember the shape of —
 * "what went wrong since last weekend" is a question with an answer, where "what
 * went wrong this month" is just a longer list.
 */
export const MISSED_DAYS = 7;

/**
 * Birds answered wrong at least once in the last `days`, in any skill.
 *
 * This is deliberately a different question from `weak`, which is next door and
 * looks similar. Weak is the scheduler's opinion of a memory right now; missed
 * is a record of something that actually happened. A bird missed on Monday and
 * drilled back to solid by Friday is missed and not weak — and it is exactly
 * the bird a Saturday round should go back to, because one clean run is not yet
 * evidence the confusion is gone.
 *
 * Not scoped to the selected packs, for the same reason `confusions` is not:
 * this describes answering that happened, whichever pack was loaded at the
 * time. Callers narrow to what is actually loaded.
 */
export async function recentMisses(
  days = MISSED_DAYS,
  now: Date = new Date(),
): Promise<Set<string>> {
  const reviews = await reviewsSince(midnight(now, days - 1).getTime());
  const missed = new Set<string>();
  for (const r of reviews) {
    if (r.correct) continue;
    const { code, skill } = parseKey(r.key);
    if (!SKILLS.includes(skill)) continue;
    missed.add(code);
  }
  return missed;
}

// ---- Per-species standing (the Library, and one bird's page) ----------------
//
// Everything above answers a question about the whole selection: how much of it
// is solid, what it will ask next, how the fortnight went. Nothing answered the
// question a learner actually asks while looking at one bird — "how am I doing
// on THIS one" — because neither the Library nor a species page read the card
// set at all. This is the same card set, sliced by species instead of by skill.

/**
 * Where one species × skill sits, in the four words the app uses for it.
 *
 * `due` outranks `solid` deliberately, and the two genuinely overlap: FSRS
 * schedules the next review for roughly the moment retrievability decays to
 * 0.9, which is the mastery threshold — so on the day a card comes up it is
 * still, by that reckoning, solid. Of the two true statements, "the app wants
 * to ask you this" is the one worth a dot.
 */
export type Standing = 'due' | 'solid' | 'weak' | 'unmet';

/** The standings a learner can filter the Library down to — solid is the rest. */
export type FilterStanding = Exclude<Standing, 'solid'>;
export const FILTER_STANDINGS: readonly FilterStanding[] = ['due', 'weak', 'unmet'] as const;

export interface SkillStanding {
  standing: Standing;
  /**
   * Chance of recalling it right now, 0–1, as the scheduler reckons it.
   * 0 for a skill that has never asked, and for a card still in learning.
   */
  retrievability: number;
  /** When this skill next asks about the bird; null when it never has. */
  due: Date | null;
}

export interface SpeciesStanding {
  code: string;
  bySkill: Record<Skill, SkillStanding>;
  /** How many of the three skills sit in each standing. */
  counts: Record<Standing, number>;
  /** No skill has ever asked about this bird — see `matchesStanding`. */
  neverMet: boolean;
  /** The soonest any of its cards comes up; null when none has been met. */
  nextDue: Date | null;
}

function unmetSkill(): SkillStanding {
  return { standing: 'unmet', retrievability: 0, due: null };
}

function standingOf(rec: CardRecord, now: Date): Standing {
  if (rec.due.getTime() <= now.getTime()) return 'due';
  if (
    rec.card.state === State.Review &&
    retrievability(rec.card, now) >= MASTERY_RETRIEVABILITY
  ) {
    return 'solid';
  }
  // Everything else: still in learning, relearning after a lapse, or a review
  // card whose memory the model has decayed past the threshold.
  return 'weak';
}

/**
 * Every selected species with its three cards read off, keyed by code.
 *
 * One pass over the whole card set for the whole Library, rather than a lookup
 * per row: the store is already in memory (see db.ts) but 140 species × 3
 * skills of individual gets is still 420 map reads and 140 promises to build a
 * list that renders in one frame.
 *
 * Species with no card at all are present and fully unmet — the absence of a
 * card IS the answer for them, and a Library row that rendered nothing would be
 * indistinguishable from one whose dots hadn't loaded.
 */
export async function speciesStandings(
  codes: string[],
  now: Date = new Date(),
): Promise<Map<string, SpeciesStanding>> {
  const out = new Map<string, SpeciesStanding>();
  for (const code of codes) {
    out.set(code, {
      code,
      bySkill: { sound: unmetSkill(), names: unmetSkill(), photo: unmetSkill() },
      counts: { due: 0, solid: 0, weak: 0, unmet: 0 },
      neverMet: true,
      nextDue: null,
    });
  }

  for (const rec of await allCards()) {
    // Scoped to the selection for the same reason mastery and the forecast are:
    // a card from a pack that isn't loaded has no row to land on.
    const sp = out.get(rec.code);
    if (!sp) continue;
    sp.bySkill[rec.skill] = {
      standing: standingOf(rec, now),
      retrievability: retrievability(rec.card, now),
      due: rec.due,
    };
  }

  for (const sp of out.values()) {
    for (const skill of SKILLS) {
      const s = sp.bySkill[skill];
      sp.counts[s.standing] += 1;
      if (s.due && (!sp.nextDue || s.due < sp.nextDue)) sp.nextDue = s.due;
    }
    sp.neverMet = sp.counts.unmet === SKILLS.length;
  }
  return out;
}

/** One bird's standing. Convenience over `speciesStandings` for its own page. */
export async function speciesStanding(
  code: string,
  now: Date = new Date(),
): Promise<SpeciesStanding> {
  return (await speciesStandings([code], now)).get(code)!;
}

/**
 * Whether a bird belongs under a Library filter chip.
 *
 * Due and weak match on ANY skill, because both name work outstanding and a
 * bird with one due card has work outstanding. Unmet is the one that reads the
 * whole bird instead: filtering on "any skill I've never been asked" would list
 * essentially the entire region for a long time — Photo ID alone lags behind
 * for months — where "birds I have never met at all" is the short, useful list
 * the word actually promises. The row's three dots tell the finer story.
 */
export function matchesStanding(sp: SpeciesStanding, filter: Standing): boolean {
  return filter === 'unmet' ? sp.neverMet : sp.counts[filter] > 0;
}

/**
 * How many birds in a set of standings each filter chip would show.
 *
 * Counted over the whole selection rather than over whatever the name filter
 * has left standing, so the numbers on the chips are a fact about the region
 * that holds still while you type — a chip whose count changed with every
 * keystroke would be answering a different question from the one it labels.
 */
export function standingTally(
  standings: Iterable<SpeciesStanding>,
): Record<FilterStanding, number> {
  const tally: Record<FilterStanding, number> = { due: 0, weak: 0, unmet: 0 };
  for (const sp of standings) {
    for (const s of FILTER_STANDINGS) if (matchesStanding(sp, s)) tally[s] += 1;
  }
  return tally;
}

/** The word the app uses for a standing, wherever it writes one out. */
export function standingWord(standing: Standing): string {
  return t(`common.standing.${standing}`);
}

/**
 * When a card next comes up, in words: "due now", "due in 3 days", or the
 * absence of a schedule at all.
 *
 * Rounded rather than precise because the schedule itself is fuzzed (FSRS runs
 * with `enable_fuzz`), so a to-the-minute promise would be a false one.
 */
export function dueLabel(due: Date | null, now: Date = new Date()): string {
  if (!due) return t('common.due.never');
  const ms = due.getTime() - now.getTime();
  if (ms <= 0) return t('common.due.now');
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return t('common.due.minutes', { n: Math.max(1, mins) });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('common.due.hours', { n: hours });
  const days = Math.round(hours / 24);
  if (days === 1) return t('common.due.tomorrow');
  if (days < 60) return t('common.due.days', { n: days });
  return t('common.due.months', { n: Math.round(days / 30) });
}

// ---- The weakest bird right now --------------------------------------------

/**
 * Answers a bird owes before it is allowed to be called the weakest one.
 *
 * Counted across all three skills, and it is the whole difference between a
 * useful line and a misleading one: a bird introduced an hour ago and answered
 * once has a rock-bottom retrievability by construction, so without a floor
 * "your weakest bird" would only ever name whatever the app showed you last.
 * Three answers is the point at which a low score is a fact about your memory
 * rather than about the bird's age.
 */
export const WEAKEST_MIN_REPS = 3;

export interface WeakestSpecies {
  code: string;
  /** Mean recall chance across the skills that have asked about it, 0–1. */
  retrievability: number;
  /** Answers across all three skills — what the floor above is applied to. */
  reps: number;
}

/**
 * The bird the learner is shakiest on, taken over all three skills at once.
 *
 * The rings say how each SKILL is going; nothing said which BIRD is the problem,
 * which is the one thing a learner can act on directly. Averaged over the skills
 * that have actually asked, not over all three — scoring an unmet skill as zero
 * would just rank birds by how few skills have met them, which is coverage
 * wearing weakness's clothes, and the Library already answers that.
 *
 * Returns null until some bird clears the floor — the honest answer early on,
 * when nothing has been seen enough for "weakest" to mean anything.
 */
export async function weakestSpecies(
  activeCodes: string[],
  now: Date = new Date(),
  minReps: number = WEAKEST_MIN_REPS,
): Promise<WeakestSpecies | null> {
  const active = new Set(activeCodes);
  const acc = new Map<string, { sum: number; cards: number; reps: number }>();

  for (const rec of await allCards()) {
    // Scoped to the selection, like every other report here: a bird from a pack
    // that isn't loaded has no name to print and isn't what you're studying.
    if (!active.has(rec.code)) continue;
    const a = acc.get(rec.code) ?? { sum: 0, cards: 0, reps: 0 };
    a.sum += retrievability(rec.card, now);
    a.cards += 1;
    a.reps += rec.card.reps;
    acc.set(rec.code, a);
  }

  let worst: WeakestSpecies | null = null;
  for (const [code, a] of acc) {
    if (a.reps < minReps) continue;
    const r = a.sum / a.cards;
    // Ties break toward the bird you've answered MORE times: same shaky memory,
    // more evidence for it.
    if (!worst || r < worst.retrievability || (r === worst.retrievability && a.reps > worst.reps)) {
      worst = { code, retrievability: r, reps: a.reps };
    }
  }
  return worst;
}
